import { Observable } from 'rxjs';
import { ChartTypes, OrgTypes, PreferenceTypes, UserTypes } from '@mode/shared/contract-common';

export abstract class AccountFacade {
  abstract hasStudioSeatLimit$: Observable<boolean>;
  abstract hasOrgSeatLimitReached$: Observable<boolean>;
  abstract hasOrganizationRestricted$: Observable<boolean>;
  abstract canCreateCollection$: Observable<boolean>;
  abstract user$: Observable<UserTypes.User>;
  abstract userPreference$: Observable<PreferenceTypes.PreferenceState>;
  abstract currentOrg$: Observable<OrgTypes.Organization>;
  abstract reportOwner$: Observable<OrgTypes.Organization>;
  abstract reportOwnerColorPalettes$: Observable<ReadonlyArray<OrgTypes.ColorPalette>>;
  abstract reportOwnerThemeSettingsLink$: Observable<string>;
  abstract reportOwnerAccountDatasetLimit$: Observable<number | undefined>;
  abstract isLoggedIn$: Observable<boolean>;
  abstract userOrgs$: Observable<ReadonlyArray<UserTypes.UserOrganization>>;
  abstract isUserOrgsLoading$: Observable<boolean>;
  abstract isUserOrgsComplete$: Observable<boolean>;
  abstract hasMemberOrgs$: Observable<boolean>;
  abstract defaultCategoricalPalette$: Observable<OrgTypes.ColorPalette>;
  abstract defaultDivergentPalette$: Observable<OrgTypes.ColorPalette>;
  abstract defaultSequentialPalette$: Observable<OrgTypes.ColorPalette>;
  abstract defaultCategoricalChartFormat$: Observable<ChartTypes.ChartFormat>;
  abstract defaultDivergentChartFormat$: Observable<ChartTypes.ChartFormat>;
  abstract defaultSequentialChartFormat$: Observable<ChartTypes.ChartFormat>;
  abstract admins$: Observable<ReadonlyArray<OrgTypes.Admin>>;
  abstract isAdminsLoading$: Observable<boolean>;
  abstract isAdminsComplete$: Observable<boolean>;
  abstract isEmailAdminSending$: Observable<boolean>;
  abstract isEmailAdminSuccess$: Observable<boolean>;
  abstract isEmailAdminError$: Observable<Error | null>;
  abstract viewedReportIds$: Observable<number[]>;
  abstract isViewedReportIdsComplete$: Observable<boolean>;

  /**
   * Sets the current username for the application and loads that user.
   * If no username is provided, it will set the state to an anonymous
   * user.
   */
  abstract loadCurrentUser(username: string | null): void;

  abstract loadUserOrgs(): void;

  abstract loadUserPreferences(): void;

  /**
   * Load the currently selected org (upper left org selector).
   */
  abstract loadCurrentOrg(username: string): void;

  /**
   * Load the organization that owns the report/exploration. Most of the time this will also end
   * up equal to the current org. One exception is if the user is viewing a report owned by an org
   * they are not a member of.
   */
  abstract loadReportOwner(username: string): void;

  /**
   * Load/refresh the report owner color palettes. Don't use on initial load, unless you
   * can guarantee report owner is loaded when you call this.
   * @deprecated should rely on the loadReportOwnerSuccess effect instead
   */
  abstract loadReportOwnerColorPalettes(): void;

  abstract loadAdminsForEmailForm(): void;

  abstract loadCollections(): void;

  abstract refreshCollections(): void;

  abstract loadViewedReportIds(username: string): void;

  abstract emailAdmin(adminId: number, message: string): void;
}
